<template>
  <div
    class="bb-fatigue-levels-hover"
    :class="{
      'bb-fatigue-levels-hover-inline': inline,
    }"
    ref="trigger"
    @mouseenter="onEnter"
    @mouseleave="scheduleHide"
    @focusin="onEnter"
    @focusout="scheduleHide"
  >
    <span
      ref="triggerAnchor"
      class="bb-fatigue-levels-trigger-anchor"
    >
      <slot name="trigger">
        <button
          type="button"
          class="btn btn-lg btn-primary bb-fatigue-levels-button"
        >
          Fatigue Levels
        </button>
      </slot>
    </span>
    <teleport
      to="body"
      v-if="inline"
    >
      <div
        v-if="showInlinePopup"
        ref="inlinePopup"
        class="card bb-card bb-fatigue-levels-popup bb-fatigue-levels-popup-fixed rounded"
        :style="inlinePopupStyle"
        @mouseenter="onEnter"
        @mouseleave="scheduleHide"
      >
        <div class="container-fluid p-3 bb-tooltip-wrapper">
          <div class="table-responsive bb-fatigue-levels-table-wrap">
            <table
              class="table table-sm table-dark table-striped mb-0 bb-fatigue-levels-table"
            >
              <thead>
                <tr>
                  <th>Fatigue Level</th>
                  <th>CON Threshold</th>
                  <th>Skills Adj</th>
                  <th>Move Adj</th>
                  <th>SR Adj</th>
                  <th>Dex Adj</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in displayRows"
                  :key="row.level"
                >
                  <td>{{ row.level }}</td>
                  <td>{{ row.conThreshold }}</td>
                  <td>{{ row.skillsAdj }}</td>
                  <td>{{ row.moveAdj }}</td>
                  <td>{{ row.srAdj }}</td>
                  <td>{{ row.dexAdj }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </teleport>
    <div
      v-else
      class="card bb-card bb-fatigue-levels-popup rounded"
    >
      <div class="container-fluid p-3 bb-tooltip-wrapper">
        <div class="table-responsive bb-fatigue-levels-table-wrap">
          <table
            class="table table-sm table-dark table-striped mb-0 bb-fatigue-levels-table"
          >
            <thead>
              <tr>
                <th>Fatigue Level</th>
                <th>CON Threshold</th>
                <th>Skills Adj</th>
                <th>Move Adj</th>
                <th>SR Adj</th>
                <th>Dex Adj</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in displayRows"
                :key="row.level"
              >
                <td>{{ row.level }}</td>
                <td>{{ row.conThreshold }}</td>
                <td>{{ row.skillsAdj }}</td>
                <td>{{ row.moveAdj }}</td>
                <td>{{ row.srAdj }}</td>
                <td>{{ row.dexAdj }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FatigueLevelsPopup',
  props: {
    baseCon: {
      type: Number,
      required: false,
      default: null,
    },
    selectedLevel: {
      type: String,
      required: false,
      default: '',
    },
    inline: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      showInlinePopup: false,
      hideTimer: null,
      inlinePopupPosition: {
        top: '50vh',
        left: '50vw',
        transform: 'none',
      },
    }
  },
  computed: {
    fatigueLevelRows() {
      const buildRow = ({
        level,
        fatiguePercent,
        skillsAdj,
        moveAdj,
        srAdj,
        dexAdj,
      }) => {
        const threshold = Number.isFinite(this.baseCon)
          ? Math.floor(this.baseCon * fatiguePercent)
          : `${Math.round(fatiguePercent * 100)}%`
        return {
          level,
          conThreshold: threshold,
          skillsAdj,
          moveAdj,
          srAdj,
          dexAdj,
        }
      }

      return [
        buildRow({
          level: 'Winded',
          fatiguePercent: 0.8,
          skillsAdj: '-10%',
          moveAdj: '0',
          srAdj: '0',
          dexAdj: '0',
        }),
        buildRow({
          level: 'Tired',
          fatiguePercent: 0.6,
          skillsAdj: '-20%',
          moveAdj: '-1m',
          srAdj: '0',
          dexAdj: '0',
        }),
        buildRow({
          level: 'Wearied',
          fatiguePercent: 0.4,
          skillsAdj: '-30%',
          moveAdj: '-1m',
          srAdj: '-2',
          dexAdj: '0',
        }),
        buildRow({
          level: 'Exhausted',
          fatiguePercent: 0.2,
          skillsAdj: '-40%',
          moveAdj: 'Half Mvt',
          srAdj: '-4',
          dexAdj: '-5',
        }),
        buildRow({
          level: 'Debilitated',
          fatiguePercent: 0.1,
          skillsAdj: '-50%',
          moveAdj: 'Half Mvt',
          srAdj: '-6',
          dexAdj: '-10',
        }),
      ]
    },
    displayRows() {
      if (!this.selectedLevel) {
        return this.fatigueLevelRows
      }

      const selectedLevel = this.normaliseLevel(this.selectedLevel)
      const matchedRow = this.fatigueLevelRows.find(
        (row) => this.normaliseLevel(row.level) === selectedLevel,
      )

      if (matchedRow) {
        return [matchedRow]
      }

      if (selectedLevel === 'fresh') {
        return [
          {
            level: 'Fresh',
            conThreshold: Number.isFinite(this.baseCon) ? this.baseCon : '100%',
            skillsAdj: '0',
            moveAdj: '0',
            srAdj: '0',
            dexAdj: '0',
          },
        ]
      }

      return []
    },
    inlinePopupStyle() {
      return {
        position: 'fixed',
        top: this.inlinePopupPosition.top,
        left: this.inlinePopupPosition.left,
        transform: this.inlinePopupPosition.transform,
        zIndex: 999999,
      }
    },
  },
  methods: {
    onEnter() {
      if (this.inline) {
        this.clearHideTimer()
        this.showInlinePopup = true
        this.$nextTick(() => {
          window.requestAnimationFrame(() => {
            this.updateInlinePopupPosition()
          })
        })
      }
    },
    onLeave() {
      if (this.inline) {
        this.clearHideTimer()
        this.showInlinePopup = false
      }
    },
    scheduleHide() {
      if (!this.inline) {
        return
      }

      this.clearHideTimer()
      this.hideTimer = window.setTimeout(() => {
        this.showInlinePopup = false
        this.hideTimer = null
      }, 120)
    },
    clearHideTimer() {
      if (this.hideTimer !== null) {
        window.clearTimeout(this.hideTimer)
        this.hideTimer = null
      }
    },
    normaliseLevel(level = '') {
      return String(level || '')
        .trim()
        .toLowerCase()
    },
    updateInlinePopupPosition() {
      const triggerAnchor = this.$refs.triggerAnchor || this.$refs.trigger
      const popup = this.$refs.inlinePopup

      const imageElement =
        triggerAnchor && typeof triggerAnchor.querySelector === 'function'
          ? triggerAnchor.querySelector('img')
          : null

      const element = imageElement || triggerAnchor

      if (!element || typeof element.getBoundingClientRect !== 'function') {
        this.inlinePopupPosition = {
          top: '50vh',
          left: '50vw',
          transform: 'translate(-50%, -50%)',
        }
        return
      }

      const rect = element.getBoundingClientRect()
      const popupRect =
        popup && typeof popup.getBoundingClientRect === 'function'
          ? popup.getBoundingClientRect()
          : null
      const viewportWidth =
        window.innerWidth || document.documentElement.clientWidth || 0
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight || 0
      const popupWidth = popupRect?.width || Math.min(864, viewportWidth - 32)
      const popupHeight = popupRect?.height || 220
      const horizontalPadding = 16
      const verticalPadding = 12
      const horizontalVisualOffset = 12
      const verticalOverlap = 8
      const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

      const leftPx = clamp(
        rect.left + rect.width / 2 - popupWidth / 2 + horizontalVisualOffset,
        horizontalPadding,
        Math.max(
          horizontalPadding,
          viewportWidth - horizontalPadding - popupWidth,
        ),
      )

      const prefersAbove = rect.top >= popupHeight + verticalPadding

      if (prefersAbove) {
        this.inlinePopupPosition = {
          top: `${Math.max(rect.top - popupHeight + verticalOverlap, verticalPadding)}px`,
          left: `${leftPx}px`,
          transform: 'none',
        }
        return
      }

      this.inlinePopupPosition = {
        top: `${Math.min(rect.bottom - verticalOverlap, viewportHeight - verticalPadding - popupHeight)}px`,
        left: `${leftPx}px`,
        transform: 'none',
      }
    },
  },
  beforeUnmount() {
    this.clearHideTimer()
  },
}
</script>

<style scoped>
.bb-fatigue-levels-hover {
  position: relative;
  z-index: 4001;
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  transform: translate(-6.4rem, 5.1rem);
}

.bb-fatigue-levels-hover-inline {
  transform: none;
  display: inline-block;
  width: fit-content;
  line-height: 0;
  vertical-align: top;
}

.bb-fatigue-levels-button {
  min-width: 12.5rem;
  padding: 0.65rem 1.2rem;
  font-size: 1.05rem;
  line-height: 1.1;
  white-space: nowrap;
}

.bb-fatigue-levels-trigger-anchor {
  display: inline-block;
  width: fit-content;
  line-height: 0;
  font-size: 0;
  vertical-align: top;
}

.bb-fatigue-levels-popup {
  position: absolute;
  z-index: 4002;
  top: calc(100% + 0.6rem);
  right: 0;
  display: none;
  width: min(54rem, 90vw);
  max-height: 32rem;
  overflow: auto;
  background: #2d3338;
  box-shadow: 0 1.35rem 2.8rem rgba(0, 0, 0, 0.48);
}

.bb-fatigue-levels-popup-inline {
  top: auto;
  bottom: calc(100% + 0.5rem);
  right: auto;
  left: 50%;
  transform: translateX(-50%);
  width: min(44rem, 90vw);
}

.bb-fatigue-levels-popup-fixed {
  display: block;
  right: auto;
  margin: 0;
  width: min(54rem, 90vw);
  max-height: min(32rem, calc(100vh - 2rem));
  overflow: auto;
}

.bb-fatigue-levels-hover:hover .bb-fatigue-levels-popup,
.bb-fatigue-levels-hover:focus-within .bb-fatigue-levels-popup {
  display: block;
}

.bb-fatigue-levels-table-wrap {
  overflow-x: auto;
}

.bb-fatigue-levels-table {
  color: #f1f3f5;
  font-size: 1.24rem;
}

.bb-fatigue-levels-table th,
.bb-fatigue-levels-table td {
  white-space: nowrap;
  text-align: center;
  vertical-align: middle;
  padding: 0.7rem 0.6rem;
}

.bb-fatigue-levels-table th:first-child,
.bb-fatigue-levels-table td:first-child {
  text-align: left;
}

@media (max-width: 1200px) {
  .bb-fatigue-levels-hover {
    transform: translate(-1.9rem, 5.1rem);
  }

  .bb-fatigue-levels-hover-inline {
    transform: none;
  }

  .bb-fatigue-levels-popup {
    right: -1rem;
  }

  .bb-fatigue-levels-popup-inline {
    right: auto;
    left: 50%;
  }
}
</style>
